export type OrderbookDepthLevel = {
  price: number
  coin_size: number
  usdt_size: number
}

export type OrderbookDisplayLevel = OrderbookDepthLevel & {
  side: 'bid' | 'ask'
}

export type OrderbookCompression =
  | { mode: 'percent'; value: number | null }
  | { mode: 'multiplier'; value: number }

type Decimal = { coefficient: bigint; exponent: number }

// Keep exchange decimal prices exact when calculating buckets. Dividing JS
// numbers directly can move a price already on a boundary to an adjacent row.
function decimalParts(value: number): Decimal {
  const [mantissa, exponent = '0'] = value.toString().toLowerCase().split('e')
  const [whole, fraction = ''] = mantissa.split('.')
  return {
    coefficient: BigInt(whole + fraction),
    exponent: Number(exponent) - fraction.length,
  }
}

function decimalNumber({ coefficient, exponent }: Decimal): number {
  return Number(`${coefficient}e${exponent}`)
}

function isValidLevel(level: OrderbookDepthLevel): boolean {
  return Number.isFinite(level.price) && level.price > 0
    && Number.isFinite(level.usdt_size) && level.usdt_size > 0
}

function resolveReferencePrice(
  bids: OrderbookDepthLevel[],
  asks: OrderbookDepthLevel[],
  referencePrice?: number | null,
): number | null {
  if (referencePrice && Number.isFinite(referencePrice) && referencePrice > 0) {
    return referencePrice
  }

  const bestBid = bids.reduce<number | null>(
    (best, level) => best === null || level.price > best ? level.price : best,
    null,
  )
  const bestAsk = asks.reduce<number | null>(
    (best, level) => best === null || level.price < best ? level.price : best,
    null,
  )
  if (bestBid !== null && bestAsk !== null) return (bestBid + bestAsk) / 2
  return bestBid ?? bestAsk
}

export function inferOrderbookTickSize(
  bids?: OrderbookDepthLevel[],
  asks?: OrderbookDepthLevel[],
  tickSize?: number | null,
): number | null {
  if (tickSize !== null && tickSize !== undefined && Number.isFinite(tickSize) && tickSize > 0) {
    return tickSize
  }

  const levels = [...(bids || []), ...(asks || [])].filter(isValidLevel)
  if (levels.length === 0) return null

  // Sparse snapshots omit empty/filtered levels, so their price gaps are not
  // exchange ticks. Decimal precision provides a conservative inferred step;
  // exchange metadata, when supplied, always takes precedence.
  const exponent = levels.reduce(
    (smallest, level) => Math.min(smallest, decimalParts(level.price).exponent),
    0,
  )
  return decimalNumber({ coefficient: 1n, exponent }) || Number.MIN_VALUE
}

function aggregateSide(
  levels: OrderbookDepthLevel[],
  side: OrderbookDisplayLevel['side'],
  step: Decimal,
): OrderbookDepthLevel[] {
  const buckets = new Map<string, OrderbookDepthLevel>()
  for (const level of levels) {
    const price = decimalParts(level.price)
    const exponent = Math.min(price.exponent, step.exponent)
    const priceUnits = price.coefficient * 10n ** BigInt(price.exponent - exponent)
    const stepUnits = step.coefficient * 10n ** BigInt(step.exponent - exponent)
    const bucket = side === 'bid'
      ? priceUnits / stepUnits
      : (priceUnits + stepUnits - 1n) / stepUnits
    const key = bucket.toString()
    const existing = buckets.get(key)
    if (existing) {
      existing.coin_size += level.coin_size
      existing.usdt_size += level.usdt_size
    } else {
      const bucketPrice = decimalNumber({ coefficient: bucket * step.coefficient, exponent: step.exponent })
      // An extreme finite multiplier can put the next ask beyond JS's price
      // range. Retain the original side rather than lose its depth or totals.
      if (!Number.isFinite(bucketPrice)) return levels
      buckets.set(key, { price: bucketPrice, coin_size: level.coin_size, usdt_size: level.usdt_size })
    }
  }
  return [...buckets.values()]
}

function selectDisplayLevels(
  bids: OrderbookDepthLevel[],
  asks: OrderbookDepthLevel[],
): OrderbookDisplayLevel[] {
  // Reserve half the rows for each side, then reuse any unfilled allocation.
  // Select nearest the spread before putting the ladder in descending order.
  const bidLimit = Math.min(bids.length, Math.max(25, 50 - asks.length))
  const askLimit = Math.min(asks.length, Math.max(25, 50 - bids.length))
  return [
    ...[...asks].sort((a, b) => a.price - b.price).slice(0, askLimit)
      .map((level) => ({ side: 'ask' as const, ...level })),
    ...[...bids].sort((a, b) => b.price - a.price).slice(0, bidLimit)
      .map((level) => ({ side: 'bid' as const, ...level })),
  ].sort((a, b) => b.price - a.price)
}

export function compressOrderbookLevels(
  bids: OrderbookDepthLevel[] | undefined,
  asks: OrderbookDepthLevel[] | undefined,
  compression: OrderbookCompression | number | null,
  referencePrice?: number | null,
  tickSize?: number | null,
): OrderbookDisplayLevel[] {
  const validBids = (bids || []).filter(isValidLevel)
  const validAsks = (asks || []).filter(isValidLevel)
  const settings: OrderbookCompression = typeof compression === 'object' && compression !== null
    ? compression
    : { mode: 'percent', value: compression }

  if (settings.mode === 'multiplier') {
    const tick = inferOrderbookTickSize(validBids, validAsks, tickSize)
    if (tick === null || !Number.isFinite(settings.value) || settings.value < 1) {
      return selectDisplayLevels(validBids, validAsks)
    }
    const tickDecimal = decimalParts(tick)
    const multiplier = decimalParts(settings.value)
    const step = {
      coefficient: tickDecimal.coefficient * multiplier.coefficient,
      exponent: tickDecimal.exponent + multiplier.exponent,
    }
    if (!Number.isFinite(decimalNumber(step))) return selectDisplayLevels(validBids, validAsks)
    return selectDisplayLevels(
      aggregateSide(validBids, 'bid', step),
      aggregateSide(validAsks, 'ask', step),
    )
  }

  const compressionPct = settings.value
  const hasCompression = compressionPct !== null
    && Number.isFinite(compressionPct)
    && compressionPct >= 0
  const resolvedReferencePrice = hasCompression
    ? resolveReferencePrice(validBids, validAsks, referencePrice)
    : null
  const rangeRatio = hasCompression ? compressionPct / 100 : null
  const minimumBidPrice = resolvedReferencePrice !== null && rangeRatio !== null
    ? resolvedReferencePrice * (1 - rangeRatio)
    : null
  const maximumAskPrice = resolvedReferencePrice !== null && rangeRatio !== null
    ? resolvedReferencePrice * (1 + rangeRatio)
    : null

  const displayBids = minimumBidPrice === null
    ? validBids
    : validBids.filter((level) => level.price >= minimumBidPrice)
  const displayAsks = maximumAskPrice === null
    ? validAsks
    : validAsks.filter((level) => level.price <= maximumAskPrice)

  return selectDisplayLevels(displayBids, displayAsks)
}
